import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Layout,
  Select,
  Card,
  Table,
  Button,
  Form,
  DatePicker,
  Input,
  Collapse,
  message,
} from "antd";
import { getStates } from "../features/statecity/statecitySlice";
import moment from "moment";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import {
  getSalary,
  createMonthSalary,
  updateASalary,
} from "../features/salary-record/salarySlice";

const { Content } = Layout;
const { Option } = Select;
const { Panel } = Collapse;

const generateFiscalYears = () => {
  const currentYear = new Date().getFullYear();
  return Array.from(
    { length: 10 },
    (_, i) => `${currentYear + i}-${currentYear + i + 1}`
  );
};

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const quarters = ["Quarter 1", "Quarter 2", "Quarter 3", "Quarter 4"];

const SalaryRecordData = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [selectedState, setSelectedState] = useState("");
  const [selectedYear, setSelectedYear] = useState(generateFiscalYears()[0]);
  const [tableData, setTableData] = useState([]);
  const [invoiceTableData, setInvoiceTableData] = useState([]);
  const [editingKey, setEditingKey] = useState("");

  useEffect(() => {
    dispatch(getStates());
    dispatch(getSalary());
  }, [dispatch]);

  const statelistState = useSelector((state) => state.statecity.statelist);
  const salarylistState = useSelector((state) => state.salary.salarys);

  useEffect(() => {
    filterSalaryData();
  }, [selectedYear, selectedState, salarylistState]);

  const filterSalaryData = () => {
    try {
      if (!salarylistState || salarylistState.length === 0) {
        setTableData([]);
        setInvoiceTableData([]);
        return;
      }

      const filteredData = salarylistState.filter(
        (record) =>
          record.sessionYear === selectedYear && record.state === selectedState
      );
      const monthlyData = filteredData.filter(
        (record) => record.salary_type === "Monthly"
      );
      const quarterlyData = filteredData.filter(
        (record) => record.salary_type === "Quarterly"
      );
      const monthlyTableData = months.map((month, index) => {
        const record = monthlyData.find((record) => record.month === month);
        return record
          ? {
              key: (index + 1).toString(),
              row_id: record._id,
              month: record.month,
              salaryDate: moment(record.salaryDate),
              paymentDate: record.paymentDate ? moment(record.paymentDate) : null,
              salaryAmount: record.salaryAmount,
              total: "",
            }
          : {
              key: (index + 1).toString(),
              row_id: "",
              month,
              salaryDate: null,
              paymentDate: null,
              salaryAmount: "",
              total: "",
            };
      });

      const quarterlyTableData = quarters.map((quarter, index) => {
        const record = quarterlyData.find((record) => record.month === quarter);
        return record
          ? {
              key: (index + 1).toString(),
              row_id: record._id,
              month: record.month,
              invoiceDate: moment(record.invoiceDate),
              invoiceAmount: record.invoiceAmount,
              totalSaving: "",
            }
          : {
              key: (index + 1).toString(),
              row_id: "",
              month: quarter,
              invoiceDate: null,
              invoiceAmount: "",
              totalSaving: "",
            };
      });

      setTableData(monthlyTableData);
      setInvoiceTableData(quarterlyTableData);
      calculateTotalSaving(monthlyTableData, quarterlyTableData);
    } catch (error) {
      console.error("An error occurred while processing the data: ", error);
    }
  };

  const calculateTotalSaving = (monthlyData, quarterlyData) => {
    const totalSalarySumByQuarter = [0, 0, 0, 0];
    const totalInvoiceSavingByQuarter = [0, 0, 0, 0];

    monthlyData.forEach((record, index) => {
      if (record.salaryAmount) {
        const quarterIndex = Math.floor(index / 3);
        totalSalarySumByQuarter[quarterIndex] += record.salaryAmount;
      }
    });

    quarterlyData.forEach((record, index) => {
      if (record.invoiceAmount) {
        totalInvoiceSavingByQuarter[index] += record.invoiceAmount;
      }
    });

    const updatedQuarterlyData = quarterlyData.map((record, index) => {
      const totalSaving =
        totalInvoiceSavingByQuarter[index] - totalSalarySumByQuarter[index];
      return { ...record, totalSaving: totalSaving };
    });

    setInvoiceTableData(updatedQuarterlyData);

    const updatedMonthlyData = monthlyData.map((record, index) => {
      const quarterIndex = Math.floor(index / 3);
      return {
        ...record,
        totals: totalSalarySumByQuarter[quarterIndex],
      };
    });

    setTableData(updatedMonthlyData);
  };

  const handleDateChange = (date, dateString, key, type) => {
    if (type === "salary") {
      const newData = [...tableData];
      const itemIndex = newData.findIndex((item) => item.key === key);
      newData[itemIndex].salaryDate = dateString ? moment(dateString) : null;
      setTableData(newData);
    } else if (type === "payment") {
      const newData = [...tableData];
      const itemIndex = newData.findIndex((item) => item.key === key);
      newData[itemIndex].paymentDate = dateString ? moment(dateString) : null;
      setTableData(newData);
    } else if (type === "invoice") {
      const newInvoiceData = [...invoiceTableData];
      const itemIndex = newInvoiceData.findIndex((item) => item.key === key);
      newInvoiceData[itemIndex].invoiceDate = dateString
        ? moment(dateString)
        : null;
      setInvoiceTableData(newInvoiceData);
    }
  };

  const handleAmountChange = (e, key, type) => {
    const value = e.target.value ? parseFloat(e.target.value) : "";
    if (type === "salary") {
      const newData = [...tableData];
      const itemIndex = newData.findIndex((item) => item.key === key);
      newData[itemIndex].salaryAmount = isNaN(value) ? "" : value;
      setTableData(newData);
    } else if (type === "invoice") {
      const newInvoiceData = [...invoiceTableData];
      const itemIndex = newInvoiceData.findIndex((item) => item.key === key);
      newInvoiceData[itemIndex].invoiceAmount = isNaN(value) ? "" : value;
      setInvoiceTableData(newInvoiceData);
    }
  };

  const handleEdit = (key) => {
    setEditingKey(key);
  };

  const handleUpdate = async (record, type) => {
    console.log("Print All row Value ----", record);
    const values = form.getFieldsValue();
    const sessionYear = values.sessionYear;
    const state = values.state;
    const modifiedValues = { ...record, sessionYear, state, salary_type: type };

    try {
      if (record.row_id === "") {
        await dispatch(createMonthSalary(modifiedValues));
      } else {
        await dispatch(updateASalary(modifiedValues));
      }
      message.success("Record updated successfully");
    } catch (error) {
      message.error("Failed to update record");
    }

    setEditingKey("");
    dispatch(getSalary());
  };

  const isEditing = (record) => record.key === editingKey;

  const handleClear = () => {
    form.resetFields();
    setSelectedState("");
    setTableData([]);
    setInvoiceTableData([]);
  };

  const handleStateChange = (value) => {
    setSelectedState(value);
    filterSalaryData();
  };

  const editableColumns = [
    {
      title: "S. No",
      dataIndex: "key",
      key: "sno",
      width: 10,
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
    },
    {
      title: "Salary Date",
      dataIndex: "salaryDate",
      key: "salaryDate",
      render: (text, record) =>
        isEditing(record) ? (
          <DatePicker
            style={{ width: "100%" }}
            value={record.salaryDate}
            onChange={(date, dateString) =>
              handleDateChange(date, dateString, record.key, "salary")
            }
          />
        ) : record.salaryDate ? (
          moment(record.salaryDate).format("YYYY-MM-DD")
        ) : (
          ""
        ),
    },
    {
      title: "Salary Due Date",
      dataIndex: "salaryDueDate",
      key: "salaryDueDate",
      render: (text, record) => {
        // Calculate last date of the month
        const monthIndex = months.indexOf(record.month);
        if (monthIndex === -1) return "";
        
        const year = selectedYear ? parseInt(selectedYear.split("-")[0]) : new Date().getFullYear();
        const lastDate = new Date(year, monthIndex + 1, 0);
        return moment(lastDate).format("YYYY-MM-DD");
      },
    },
    {
      title: "Salary Paid Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (text, record) =>
        isEditing(record) ? (
          <DatePicker
            style={{ width: "100%" }}
            value={record.paymentDate}
            onChange={(date, dateString) =>
              handleDateChange(date, dateString, record.key, "payment")
            }
          />
        ) : record.paymentDate ? (
          moment(record.paymentDate).format("YYYY-MM-DD")
        ) : (
          ""
        ),
    },
    {
      title: "Salary Amount",
      dataIndex: "salaryAmount",
      key: "salaryAmount",
      render: (text, record) =>
        isEditing(record) ? (
          <Input
            value={record.salaryAmount}
            onChange={(e) => handleAmountChange(e, record.key, "salary")}
            placeholder="Enter amount"
          />
        ) : (
          record.salaryAmount
        ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) =>
        isEditing(record) ? (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => handleUpdate(record, "Monthly")}
          >
            Update
          </Button>
        ) : (
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record.key)}
          >
            Edit
          </Button>
        ),
    },
  ];

  const invoiceEditableColumns = [
    {
      title: "S. No",
      dataIndex: "key",
      key: "sno",
      width: 10,
    },
    {
      title: "Quarter",
      dataIndex: "month",
      key: "month",
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (text, record) =>
        isEditing(record) ? (
          <DatePicker
            style={{ width: "100%" }}
            value={record.invoiceDate}
            onChange={(date, dateString) =>
              handleDateChange(date, dateString, record.key, "invoice")
            }
          />
        ) : record.invoiceDate ? (
          moment(record.invoiceDate).format("YYYY-MM-DD")
        ) : (
          ""
        ),
    },
    {
      title: "Invoice Amount",
      dataIndex: "invoiceAmount",
      key: "invoiceAmount",
      render: (text, record) =>
        isEditing(record) ? (
          <Input
            value={record.invoiceAmount}
            onChange={(e) => handleAmountChange(e, record.key, "invoice")}
            placeholder="Enter amount"
          />
        ) : (
          record.invoiceAmount
        ),
    },
    {
      title: "Total Saving",
      dataIndex: "totalSaving",
      key: "totalSaving",
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) =>
        isEditing(record) ? (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => handleUpdate(record, "Quarterly")}
          >
            Update
          </Button>
        ) : (
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record.key)}
          >
            Edit
          </Button>
        ),
    },
  ];

  return (
    <Layout style={{ backgroundColor: "#eaedf2", minHeight: "100vh", fontFamily: "Poppins, sans-serif" }}>
      <Layout style={{ padding: "20px 24px", backgroundColor: "#eaedf2" }}>
        <h3 className="mb-4 title">Salary Records</h3>
        <Content
          className="site-layout-background"
          style={{
            padding: "20px",
            margin: 0,
            backgroundColor: "#ffffff",
            border: "1px solid #eef2f6",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          }}
        >
          <Form form={form} layout="inline" style={{ marginBottom: 20 }}>
            <Form.Item
              name="sessionYear"
              label="Session Year"
              initialValue={selectedYear}
            >
              <Select
                placeholder="Select Session Year"
                style={{ width: 200 }}
                defaultValue={selectedYear}
                onChange={(value) => setSelectedYear(value)}
              >
                {generateFiscalYears().map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="state" label="State">
              <Select
                placeholder="Select State"
                style={{ width: 200 }}
                onChange={handleStateChange}
              >
                {statelistState.map((i) => (
                  <Select.Option key={i.code} value={i.code}>
                    {i.state_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button type="default" onClick={handleClear}>
                Clear
              </Button>
            </Form.Item>
          </Form>

          {selectedState && (
            <Collapse defaultActiveKey={["1"]}>
              {Array.from({ length: 4 }, (_, i) => (
                <Panel header={`Quarter ${i + 1}`} key={i + 1}>
                  <Card
                    title={`Quarter ${i + 1} Salary Details`}
                    bordered={false}
                    style={{ marginBottom: 20 }}
                  >
                    <Table
                      className="custom-table"
                      columns={editableColumns}
                      dataSource={tableData.slice(i * 3, (i + 1) * 3)}
                      pagination={false}
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}></Table.Summary.Cell>
                          <Table.Summary.Cell index={1}></Table.Summary.Cell>
                          <Table.Summary.Cell index={2}></Table.Summary.Cell>
                          <Table.Summary.Cell index={3}></Table.Summary.Cell>
                          <Table.Summary.Cell index={4}></Table.Summary.Cell>
                          <Table.Summary.Cell index={5}></Table.Summary.Cell>
                          <Table.Summary.Cell index={6}>
                            {" "}
                            <strong>Total = </strong>
                            {tableData
                              .slice(i * 3, (i + 1) * 3)
                              .reduce(
                                (sum, record) =>
                                  sum + (record.salaryAmount || 0),
                                0
                              )}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={7}></Table.Summary.Cell>
                          <Table.Summary.Cell index={8}></Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  </Card>
                  <Card
                    title={`Quarter ${i + 1} Invoice Details`}
                    bordered={false}
                  >
                    <Table
                      className="custom-table"
                      columns={invoiceEditableColumns}
                      dataSource={invoiceTableData.slice(i, i + 1)}
                      pagination={false}
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}></Table.Summary.Cell>
                          <Table.Summary.Cell index={1}></Table.Summary.Cell>
                          <Table.Summary.Cell index={2}></Table.Summary.Cell>
                          <Table.Summary.Cell index={3}></Table.Summary.Cell>
                          <Table.Summary.Cell index={4}>
                            {" "}
                            <strong>Total Saving = </strong>
                            {invoiceTableData[i].totalSaving}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5}></Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  </Card>
                </Panel>
              ))}
            </Collapse>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default SalaryRecordData;
